import { CSVColumn, CSVHeaderMap } from './csvProcessor';

// "Job #", "Job No." and "job_number" all reduce to "job".
function normalizeHeader(header: string) {
  return header
    .toLowerCase()
    .replace(/#/g, ' ')
    .replace(/\bno\./g, ' ')
    .split(/[\s_]+/)
    .filter((word) => word && word !== 'number')
    .join('');
}

export function matchColumnsToHeaders(columns: CSVColumn[], headers: string[]) {
  const mapping: CSVHeaderMap = {};
  const usedHeaders = new Set<string>();

  const matchBy = (isMatch: (header: string, column: CSVColumn) => boolean) => {
    columns.forEach((column) => {
      if (mapping[column.key]) return;

      const matchingHeader = headers.find(
        (header) => !usedHeaders.has(header) && isMatch(header, column),
      );
      if (matchingHeader) {
        mapping[column.key] = matchingHeader;
        usedHeaders.add(matchingHeader);
      }
    });
  };

  matchBy((header, column) => header.toLowerCase() === column.header.toLowerCase());
  matchBy((header, column) => normalizeHeader(header) === normalizeHeader(column.header));
  matchBy((header, column) =>
    Boolean(
      column.headerAliases?.some(
        (alias) => normalizeHeader(alias) === normalizeHeader(header),
      ),
    ),
  );

  return mapping;
}

const storageKeyFor = (entityType: string) => `csvColumnMapping.${entityType}`;

const headerSetKeyFor = (headers: string[]) => JSON.stringify([...headers].sort());

function readSavedMappings(entityType: string): Record<string, unknown> {
  try {
    const savedMappings = JSON.parse(
      localStorage.getItem(storageKeyFor(entityType)) ?? '{}',
    );
    return savedMappings && typeof savedMappings === 'object' ? savedMappings : {};
  } catch {
    return {};
  }
}

// A saved null is a column the user cleared, so it is not auto-matched.
export function loadSavedColumnMapping(
  entityType: string,
  columns: CSVColumn[],
  headers: string[],
) {
  const savedMapping = readSavedMappings(entityType)[headerSetKeyFor(headers)];
  if (!savedMapping || typeof savedMapping !== 'object') return null;

  const savedEntries = Object.entries(savedMapping).filter(
    ([columnKey, header]) =>
      columns.some((column) => column.key === columnKey) &&
      (header === null || (typeof header === 'string' && headers.includes(header))),
  );
  const savedColumnKeys = new Set(savedEntries.map(([columnKey]) => columnKey));
  const savedHeaders = savedEntries.flatMap(([, header]) =>
    typeof header === 'string' ? [header] : [],
  );
  const autoMapping = matchColumnsToHeaders(
    columns.filter((column) => !savedColumnKeys.has(column.key)),
    headers.filter((header) => !savedHeaders.includes(header)),
  );

  return {
    ...autoMapping,
    ...Object.fromEntries(
      savedEntries.filter((entry): entry is [string, string] => entry[1] !== null),
    ),
  };
}

export function saveColumnMapping(
  entityType: string,
  headers: string[],
  mapping: Record<string, string | null>,
) {
  try {
    localStorage.setItem(
      storageKeyFor(entityType),
      JSON.stringify({
        ...readSavedMappings(entityType),
        [headerSetKeyFor(headers)]: mapping,
      }),
    );
  } catch {
    // The saved mapping only pre-fills the next import, so a full or blocked storage is ignored.
  }
}
